import request from './requestServices';

export const usageService = {
  /**
   * Get pod usage for current user
   */
  async getPodUsage(hours = 24) {
    try {
      const response = await request(`/v1/usage/pod-usage?hours=${hours}`);
      return response;
    } catch (error) {
      console.error('Failed to get pod usage:', error);
      throw error;
    }
  },

  /**
   * Get EC2 usage for current user (legacy)
   */
  async getEC2Usage(hours = 24) {
    try {
      const response = await request(`/v1/usage/ec2-usage?hours=${hours}`);
      return response;
    } catch (error) {
      console.error('Failed to get EC2 usage:', error);
      throw error;
    }
  },

  /**
   * Get LLM usage for current user
   */
  async getLLMUsage(hours = 24) {
    try {
      const response = await request(`/v1/usage/llm-usage?hours=${hours}`);
      return response;
    } catch (error) {
      console.error('Failed to get LLM usage:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive usage summary
   */
  async getUsageSummary(hours = 24) {
    try {
      const response = await request(`/v1/usage/usage-summary?hours=${hours}`);
      return response;
    } catch (error) {
      console.error('Failed to get usage summary:', error);
      throw error;
    }
  },

  /**
   * Get usage history
   */
  async getUsageHistory(days = 7, resourceType = null) {
    try {
      let url = `/v1/usage/usage-history?days=${days}`;
      if (resourceType) {
        url += `&resource_type=${resourceType}`;
      }
      const response = await request(url);
      return response;
    } catch (error) {
      console.error('Failed to get usage history:', error);
      throw error;
    }
  },

  /**
   * Get Stripe meter configuration
   */
  async getStripeMeters() {
    try {
      const response = await request(`/v1/usage/stripe-meters`);
      return response;
    } catch (error) {
      console.error('Failed to get Stripe meters:', error);
      throw error;
    }
  },

  /**
   * Get all usage-based pricing (single source of truth from AWS Secrets Manager)
   */
  async getUsagePricing() {
    try {
      const response = await request(`/v1/usage/usage-pricing`);
      return response;
    } catch (error) {
      console.error('Failed to get usage pricing:', error);
      throw error;
    }
  },

  /**
   * Get server pod pricing only
   */
  async getServerPodPricing() {
    try {
      const response = await request(`/v1/usage/usage-pricing/server-pods`);
      return response;
    } catch (error) {
      console.error('Failed to get server pod pricing:', error);
      throw error;
    }
  },

  /**
   * Get LLM token pricing only
   */
  async getLLMTokenPricing() {
    try {
      const response = await request(`/v1/usage/usage-pricing/llm-tokens`);
      return response;
    } catch (error) {
      console.error('Failed to get LLM token pricing:', error);
      throw error;
    }
  },

  /**
   * Report LLM usage (for manual reporting)
   */
  async reportLLMUsage(usageData) {
    try {
      const response = await request(`/v1/usage/report-llm-usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(usageData),
      });
      return response;
    } catch (error) {
      console.error('Failed to report LLM usage:', error);
      throw error;
    }
  },

  /**
   * Get usage chart data for visualization
   */
  async getUsageChartData(hours = 24) {
    try {
      const response = await request(`/v1/usage/usage-chart-data?hours=${hours}`);
      return response;
    } catch (error) {
      console.error('Failed to get usage chart data:', error);
      throw error;
    }
  },

  /**
   * Get available features for current user's plan
   */
  async getAvailableFeatures() {
    try {
      const response = await request(`/v1/usage/access/features`);
      return response;
    } catch (error) {
      console.error('Failed to get available features:', error);
      throw error;
    }
  },

  /**
   * Check if user has access to a specific feature
   */
  async checkFeatureAccess(feature) {
    try {
      const response = await request(`/v1/usage/access/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ feature }),
      });
      return response;
    } catch (error) {
      console.error('Failed to check feature access:', error);
      throw error;
    }
  },

  /**
   * Get quota status for a resource type
   */
  async getQuota(resourceType) {
    try {
      const response = await request(`/v1/usage/quota/${resourceType}`);
      return response;
    } catch (error) {
      console.error('Failed to get quota:', error);
      throw error;
    }
  },

  /**
   * Check if quota allows an action
   */
  async checkQuota(resourceType, quantity) {
    try {
      const response = await request(`/v1/usage/quota/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resource_type: resourceType, quantity }),
      });
      return response;
    } catch (error) {
      console.error('Failed to check quota:', error);
      throw error;
    }
  },

  /**
   * Get Stripe product details (pricing, limits, etc.)
   * Single source of truth for product information
   */
  async getStripeProducts(priceId = null) {
    try {
      let url = `/v1/usage/products`;
      if (priceId) {
        url += `?price_id=${priceId}`;
      }
      const response = await request(url);
      return response;
    } catch (error) {
      console.error('Failed to get Stripe products:', error);
      throw error;
    }
  },

  /**
   * Get usage summary for the current billing cycle only.
   * This is independent of the date picker and always shows data from
   * the current subscription billing period.
   */
  async getCurrentCycleSummary() {
    try {
      const response = await request(`/v1/usage/current-cycle-summary`);
      return response;
    } catch (error) {
      console.error('Failed to get current cycle usage summary:', error);
      throw error;
    }
  }
};

export default usageService;
